import {
  RiskAssessmentProviderEnum,
  RiskLevelEnum,
  TaskPriorityEnum,
  TaskResolutionEnum,
  TaskStatusEnum,
  TaskTypeEnum,
} from '@olv/ui-shared/enums/task.enums';
import { RiskGroupShortCodeEnum } from '@olv/ui-shared/enums/risk-group-short-code.enum';
import { Transaction } from '@olv/ui-shared/models/merchant-transaction.model';
import { WalletOwnerTypeEnum } from './wallet.model';
import type { WhitelistedWalletResponseDto } from '@olv/ui-shared/services/merchant.service';

// Frontend interfaces
export interface MediaFile {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  uploadedAt: Date;
  uploadedByUserId: string;
}

export interface Task {
  whitelistedWalletDetail?: WhitelistedWalletResponseDto;
  transactionDetail?: Transaction;
  delegationDetail?: DelegationTaskDetail;
  preLaunchEvmBalanceDetail?: PreLaunchEvmBalanceDetail;
  id: string;
  title: string;
  description: string;
  priority: TaskPriorityEnum;
  status: TaskStatusEnum;
  type: TaskTypeEnum;

  initiatedByDisplayName: string;
  initiatedByUserId: string;
  assignedToMerchantId?: string;

  dueDate: Date;
  createdDate: Date;
  updatedDate: Date;
  resolvedAt?: Date;

  relatedMerchantId?: string;
  relatedTransactionId?: string;
  relatedWalletId?: string;
  relatedMerchantName?: string;
  relatedWalletName?: string;
  walletAddress?: string;

  resolvedByUserId?: string;
  resolvedByDisplayName?: string;
  resolution?: TaskResolutionEnum;
  comment?: string;
  riskLevel?: RiskLevelEnum;

  attachmentIds: string[];
  isAcknowledgeOnly?: boolean;
}

export interface PreLaunchEvmBalanceDetail {
  merchantName?: string;
  networkName?: string;
  walletName?: string;
  walletAddress?: string;
  currencySymbol?: string;
  currencyDisplayPrecision?: number;
  unrecordedAmount?: number;
  onChainBalance?: number;
  recordedBySystem?: number;
  detectedAt?: string;
  needsManualReview?: boolean;
  isTransfer?: boolean;
  twinWalletId?: string;
  walletOwnerType?: WalletOwnerTypeEnum;
  transactionHash?: string;
  blockNumber?: number;
  transactionTime?: string;
}

export interface DelegationTaskDetail {
  networkId: string;
  networkName?: string;
  merchantId?: string;
  merchantName?: string;
  riskGroupShortCode?: RiskGroupShortCodeEnum;
  resourceType: number;
  configuredInResourceUnits?: number;
  heldInResourceUnits?: number;
  topUpInResourceUnits?: number;
  fundedFromPotInResourceUnits?: number;
  fundedFromReserveInResourceUnits?: number;
  shortfallInResourceUnits?: number;
}

export interface MerchantChangeTaskDetail {
  id: string;
  taskId: string;
  merchantId: string;
  oldValues: {
    name?: string;
    profile?: string;
    feeProfile?: string;
    [key: string]: any;
  };
  newValues: {
    name?: string;
    profile?: string;
    feeProfile?: string;
    [key: string]: any;
  };
  changedFields: string[];
}

// History-specific versions that include resolution information
export interface MerchantChangeHistoryDetail extends MerchantChangeTaskDetail {
  appliedValues?: {
    name?: string;
    profile?: string;
    feeProfile?: string;
    [key: string]: any;
  };
  rejectionReason?: string;
}

export interface RiskAlertTaskDetail {
  id: string;
  taskId: string;
  relatedWalletId?: string;
  relatedTransactionId?: string;
  relatedWhitelistedWalletId?: string;
  previousRiskLevel: RiskLevelEnum;
  currentRiskLevel: RiskLevelEnum;
  reportUrl?: string;
  riskAssessmentProvider: RiskAssessmentProviderEnum;
  createdAt: Date;
  updatedAt: Date;
}

export type TaskDetail = MerchantChangeTaskDetail | WhitelistedWalletResponseDto | RiskAlertTaskDetail | Transaction;

// Backend DTOs matching the C# backend models
export interface TaskDto {
  id: string;
  title: string;
  description: string;
  priority: TaskPriorityEnum;
  status: TaskStatusEnum;
  type: TaskTypeEnum;

  assigneeKeycloakUserId: string;
  initiatedByDisplayName: string;
  initiatedByUserId: string;
  assignedToEntity: string;
  assignedToMerchantId?: string;

  dueDate: string; // ISO date string
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  resolvedAt?: string; // ISO date string

  relatedMerchantId?: string;
  relatedTransactionId?: string;
  relatedWhitelistedWalletId?: string;
  relatedWalletId?: string;
  relatedMerchantName?: string;
  relatedWalletName?: string;

  resolvedByUserId?: string;
  resolvedByDisplayName?: string;
  resolution?: TaskResolutionEnum;
  comment?: string;

  attachmentIds: string[];
  attachments: MediaFileDto[];

  isAcknowledgeOnly?: boolean;

  // Task details based on type
  merchantChangeDetail?: MerchantChangeTaskDetail;
  whitelistedWalletDetail?: WhitelistedWalletResponseDto;
  riskAlertDetail?: RiskAlertTaskDetail;
  transactionDetail?: Transaction;
  delegationDetail?: DelegationTaskDetail;
  preLaunchEvmBalanceDetail?: PreLaunchEvmBalanceDetail;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  priority?: TaskPriorityEnum;
  status?: TaskStatusEnum;
  assigneeKeycloakUserId?: string;
  dueDate?: string; // ISO date string
  attachmentIds?: string[];
}

export interface ResolveTaskDto {
  resolution: TaskResolutionEnum;
  comment?: string;
  attachmentIds?: string[];
  totp?: string;
}

export interface AcknowledgeTaskDto {
  comment?: string;
}

export interface MediaFileDto {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  uploadedAt: string;
  uploadedByUserId: string;
}

export interface TaskMerchantDto {
  id: string;
  name: string;
}

export interface TaskUserFilterDto {
  userId: string;
  displayName: string;
}

export interface TaskUsersFilterResponseDto {
  preparedBy: TaskUserFilterDto[];
  resolvedBy: TaskUserFilterDto[];
}

// Filter parameters for frontend
export interface TaskFilterParams {
  searchText?: string;
  status?: TaskStatusEnum[];
  type?: TaskTypeEnum[];
  priority?: TaskPriorityEnum;
  assigneeKeycloakUserId?: string;
  assignedToMerchantId?: string;
  relatedMerchantId?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  riskLevel?: RiskLevelEnum;
  createdAtFrom?: string;
  createdAtTo?: string;
  preparedBy?: string[];
  resolvedBy?: string[];
  resolvedAtFrom?: string;
  resolvedAtTo?: string;
}

export interface GetHistoryTasksInput extends TaskFilterParams {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
}

export type GetPendingTasksInput = Omit<GetHistoryTasksInput, 'resolvedBy' | 'resolvedAtFrom' | 'resolvedAtTo'>;

// Mapper utility class to convert between backend DTOs and frontend models
export class TaskMapper {
  static toFrontendTask(dto: TaskDto): Task {
    return {
      id: dto.id,
      title: dto.title,
      description: dto.description,
      priority: dto.priority,
      status: dto.status,
      type: dto.type,
      initiatedByDisplayName: dto.initiatedByDisplayName,
      initiatedByUserId: dto.initiatedByUserId,
      assignedToMerchantId: dto.assignedToMerchantId,
      dueDate: new Date(dto.dueDate),
      createdDate: new Date(dto.createdAt),
      updatedDate: new Date(dto.updatedAt),
      resolvedAt: dto.resolvedAt ? new Date(dto.resolvedAt) : undefined,
      relatedTransactionId: dto.relatedTransactionId,
      relatedWalletId: dto.relatedWalletId,
      relatedMerchantName: dto.relatedMerchantName,
      relatedWalletName: dto.relatedWalletName,
      whitelistedWalletDetail: dto.whitelistedWalletDetail,
      transactionDetail: dto.transactionDetail,
      preLaunchEvmBalanceDetail: dto.preLaunchEvmBalanceDetail,
      resolvedByUserId: dto.resolvedByUserId,
      resolvedByDisplayName: dto.resolvedByDisplayName,
      resolution: dto.resolution,
      comment: dto.comment,
      attachmentIds: dto.attachmentIds,
      isAcknowledgeOnly: dto.isAcknowledgeOnly,
    };
  }
}
